import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import {
  EmissionsHomeViewFilterAreaSchemaKeys,
  type EmissionsHomeViewFilterAreaSchemaType,
} from "#src/batteries-included-components/FilterAreas/DashboardsFilterAreas/DashboardsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import { useQuery } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  StorageKeys,
} from "@validereinc/common-components";
import {
  CalculatorResultType,
  CalculatorResultsDomain,
  SortDirection,
} from "@validereinc/domain";
import { Measurements } from "@validereinc/domain-controllers";
import { get } from "es-toolkit/compat";
import React from "react";

const { PollutantsEmitted } = Measurements.logic;

export const FacilityEmissionsByPollutantDataTablePanel = ({
  viewConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey">) => {
  const [filters] = useStickyState<EmissionsHomeViewFilterAreaSchemaType>(
    {},
    viewConfigStorageKey
  );
  const { localize } = useLocalization();
  const {
    getUnitByType,
    getPrecisionByType,
    getUnitName,
    isLoading: isMeasurementsLoading,
  } = useMeasurementTypes();

  const {
    [EmissionsHomeViewFilterAreaSchemaKeys.date_range]: dateRange,
    [EmissionsHomeViewFilterAreaSchemaKeys.pollutant]: pollutant,
    [EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario]: reportingGroup,
  } = filters;
  const { mass, label } = pollutant
    ? (PollutantsEmitted[pollutant] ?? PollutantsEmitted.co2_eq)
    : PollutantsEmitted.co2_eq;
  const precision = getPrecisionByType(mass);
  const unit = getUnitName(getUnitByType(mass));

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      page: 1,
      pageSize: 10,
      sortBy: `measurement.${mass}`,
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const request = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      measurement_type: {
        $in: ["mass_ch4", "mass_co2", "mass_co2eq", "mass_n2o"],
      },
      reporting_group: reportingGroup,
      year_month: {
        // @ts-expect-error TODO: date format is not correct
        $in: getIntervalInMonthsFormatted(hydrateDateRange(dateRange)),
      },
      isAlreadyFormatted: true,
    },
    groupBy: ["equipment.facility.id", "equipment.facility.name"],
  };

  const {
    data,
    isLoading: isResultsLoading,
    isFetching: isResultsFetching,
  } = useQuery({
    queryKey: ["calculatorResults", request],
    queryFn: () => CalculatorResultsDomain.getList(request as any),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const pollutantHeader: HeaderType<CalculatorResultType> = {
    label: `${label}${unit ? ` [${unit}]` : ""}`,
    key: `measurement.${mass}`,
    isSortable: true,
    alignment: "right",
    renderComponent: ({ item, dataKey }) => (
      <DataTable.DataRow.NumberCell
        value={get(item, dataKey)}
        formattingOpts={
          precision
            ? {
                fractionDigits: precision,
              }
            : {}
        }
      />
    ),
  };

  const headers: Array<HeaderType<CalculatorResultType>> = [
    {
      label: `${localize("Facility")} Name`,
      key: "equipment.facility.name",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFacilityDetail(item?.["equipment.facility.id"])}>
          {item?.["equipment.facility.name"]}
        </RoutingLink>
      ),
    },
    pollutantHeader,
  ];

  return (
    <DataTablePanel
      panelProps={{ title: "Emissions by Facility" }}
      dataTableProps={{
        headers,
        items: data?.data ?? [],
        getItemId: (item) => item["equipment.facility.id"] ?? "",
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        isLoading: isResultsLoading || isMeasurementsLoading,
        isBusy: isResultsFetching,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: data?.total_entries ?? 0,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
    />
  );
};
